import React, {Fragment, useContext, useEffect, useState} from "react";
import {connect} from "react-redux";
import {FormattedMessage, injectIntl} from "react-intl";
import QRCode from "qrcode";

import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";

import {LOOK_OVERLAYS} from "../../../look/core/constants";
import Config, {getCommonVisualizerType, IS_QKB_UI} from "../../../../config";
import {setAiUserFileBlobUrl} from "../../aiActions";
import LoadingSpinner from "../../../ui/loadingSpinner";
import LookAiPopupContext from "../lookAIPopup/LookAiPopupContext";
import {applyLookAiUserFile} from "../lookAIPopup/lookAIUserFile";
import {buildMobilePhotoUrl, imageUrlToFile, requestMobilePhotoGuid, startMobilePhotoPolling} from "./lookAIQrHelpers";


const QR_STATUS = {
  loading: "loading", // waiting for the GUID
  qr: "qr", // QR code is shown, polling
  receiving: "receiving", // the phone has uploaded, loading the image into the popup
  expired: "expired",
  error: "error",
};

const QR_SIZE = 280;

const LookAIQrOverlay = (props) => {

  const {lookAiVisited, lookOverlayOpened, aiImageExpanded, isMobile, jobId, intl, setAiUserFileBlobUrl} = props;

  const {lookAiPopupContext, lookAiPopupDispatch} = useContext(LookAiPopupContext);
  const {qrPanelOpened, qrSessionKey} = lookAiPopupContext;

  const [status, setStatus] = useState(QR_STATUS.loading);
  const [qrUrl, setQrUrl] = useState("");
  const [qrImage, setQrImage] = useState("");
  const [errorId, setErrorId] = useState("");

  const aiOpened = (lookOverlayOpened === LOOK_OVERLAYS.aiPanel);
  const panelVisible = !!qrPanelOpened && aiOpened && !aiImageExpanded && !isMobile;

  // closing the AI popup also closes the QR panel, so it doesn't pop up again on the next popup open
  useEffect(() => {
    if (!aiOpened && qrPanelOpened) {
      lookAiPopupDispatch({qrPanelOpened: false});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiOpened]);

  // a fresh GUID for every open / regenerate, the cleanup stops polling on close and unmount
  useEffect(() => {
    if (!panelVisible) {
      return undefined;
    }
    let cancelled = false;
    let stopPolling = null;

    const showError = (id) => {
      if (cancelled) {
        return
      }
      setErrorId(id);
      setStatus(QR_STATUS.error);
    };

    const onImage = async (imageUrl) => {
      if (cancelled) {
        return
      }
      setStatus(QR_STATUS.receiving);
      try {
        const file = await imageUrlToFile(imageUrl);
        if (cancelled) {
          return
        }
        applyLookAiUserFile(file, lookAiPopupDispatch, setAiUserFileBlobUrl);
        lookAiPopupDispatch({qrPanelOpened: false});
      } catch (error) {
        console.error("LookAIQrOverlay: mobile photo download error", error);
        showError("look.ai.qr.error.download");
      }
    };

    const start = async () => {
      setStatus(QR_STATUS.loading);
      setQrUrl("");
      setQrImage("");
      setErrorId("");
      let guid;
      try {
        guid = await requestMobilePhotoGuid(jobId);
      } catch (error) {
        console.error("LookAIQrOverlay: GUID error", error);
        showError("look.ai.qr.error.guid");
        return
      }
      if (cancelled) {
        return
      }
      const url = buildMobilePhotoUrl(guid);
      let dataUrl;
      try {
        dataUrl = await QRCode.toDataURL(url, {width: QR_SIZE, margin: 0, errorCorrectionLevel: "M"});
      } catch (error) {
        console.error("LookAIQrOverlay: QR generation error", error);
        showError("look.ai.qr.error.guid");
        return
      }
      if (cancelled) {
        return
      }
      setQrUrl(url);
      setQrImage(dataUrl);
      setStatus(QR_STATUS.qr);
      stopPolling = startMobilePhotoPolling(guid, {
        onImage,
        onExpired: () => {
          if (!cancelled) {
            setStatus(QR_STATUS.expired);
          }
        },
        onError: (reason) => showError(reason === "notFound" ? "look.ai.qr.error.notFound" : "look.ai.qr.error.network"),
      });
    };

    start();

    return () => {
      cancelled = true;
      if (stopPolling) {
        stopPolling();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelVisible, qrSessionKey]);

  if (!lookAiVisited || isMobile) {
    return null
  }

  const closeQrPanel = () => {
    lookAiPopupDispatch({qrPanelOpened: false});
  };

  const regenerateQr = () => {
    lookAiPopupDispatch({qrSessionKey: (qrSessionKey || 0) + 1});
  };

  const visualizerType = intl.formatMessage({id: "rooms." + getCommonVisualizerType()});

  const renderContent = () => {
    switch (status) {
      case QR_STATUS.qr:
        return (
          <Fragment>
            {Config.isDevMode() ? (
              <a href={qrUrl}
                 target="_blank"
                 rel="noopener noreferrer"
                 className="block cursor-pointer bg-white u-p-05 look-ai__qr-code">
                <img src={qrImage} width={QR_SIZE} height={QR_SIZE} alt="QR code" className="block"/>
              </a>
            ) : (
              <div className="bg-white u-p-05 look-ai__qr-code">
                <img src={qrImage} width={QR_SIZE} height={QR_SIZE} alt="QR code" className="block"/>
              </div>
            )}
            <p className="look-ai__text font--12 text-center u-leading-1 u-mt-1 u-mb-0 look-ai__qr-caption">
              <FormattedMessage id="look.ai.qr.caption" values={{visualizerType}}/>
            </p>
          </Fragment>
        );
      case QR_STATUS.expired:
      case QR_STATUS.error:
        return (
          <Fragment>
            <p className="look-ai__text font--14 text-center u-leading-1 u-mt-0 u-mb-1 look-ai__qr-caption">
              <FormattedMessage id={status === QR_STATUS.expired ? "look.ai.qr.expired" : (errorId || "look.ai.qr.error.guid")}/>
            </p>
            <Button className="look-button look-button--primary" variant="contained" onClick={regenerateQr}>
              <FormattedMessage id="look.ai.qr.regenerate"/>
            </Button>
          </Fragment>
        );
      case QR_STATUS.receiving:
        return (
          <Fragment>
            <LoadingSpinner/>
            <p className="look-ai__text font--12 text-center u-leading-1 u-mt-1 u-mb-0">
              <FormattedMessage id="look.ai.qr.receiving"/>
            </p>
          </Fragment>
        );
      default:
        return <LoadingSpinner/>;
    }
  };

  return (
    <div className={"fixed transition2 u-h-full u-top-0 look-ai__images-panel look-ai__images-panel--qr flex flex--column"
      + (aiOpened && !aiImageExpanded ? "" : " u-left-0")
      + (IS_QKB_UI ? " u-left-0 look-ai__images-panel--qkb" : "")
      + (panelVisible ? " look-ai__images-panel--qr-opened" : "")}>
      <div className="look-ai__content u-w-full u-p-1 bg-white flex--1 flex flex--column shadow-md">
        <div className="w-full flex flex--center flex--end u-mt-05">
          <IconButton aria-label={intl.formatMessage({id: "iconButton.close"})}
                      className="look-icon look-icon--small"
                      onClick={closeQrPanel}>
            <CloseIcon/>
          </IconButton>
        </div>
        <div className="flex--1 flex flex--column flex--center u-pb-1">
          {renderContent()}
        </div>
      </div>
    </div>
  )
};

const mapStateToProps = (store) => {
  const {lookOverlayOpened} = store.lookState;
  const {lookAiVisited, aiImageExpanded} = store.aiState;
  const {isMobile, jobId} = store.serverState;

  return {
    lookOverlayOpened,
    lookAiVisited,
    aiImageExpanded,
    isMobile,
    jobId,
  };
};

const mapDispatchToProps = {
  setAiUserFileBlobUrl,
};

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(LookAIQrOverlay)
)
